import { FC, Fragment, useEffect, useState } from 'react';
import { Controller, useController } from 'react-hook-form';
import { Plus } from 'react-feather';
import { FormattedMessage, useIntl } from 'react-intl';
import { useDispatch, useSelector } from 'react-redux';
import { Col, InputGroupText, Spinner, Tooltip } from 'reactstrap';
import * as authSelectors from '@redux/reducers/auth';
import * as agentsSelectors from 'views/screens/Agents/reducers';
import * as divisionSelectors from 'views/screens/Divisions/reducers';
import FetchEntities, { DefaultFilters } from 'views/screens/Entities/views/FetchEntities';
import * as groupsSelectors from 'views/screens/Groups/reducers';
import * as operatorSelectors from 'views/screens/Operators/reducers';
import * as organizationSelectors from 'views/screens/Organizations/reducers';
import * as subdivisionSelectors from 'views/screens/Subdivisions/reducers';
import * as organizationActions from 'views/screens/Organizations/actions';
import * as organizationTypes from 'views/screens/Organizations/types';
import organizationPermissions from 'views/screens/Organizations/permissions';
import SlidingPaneQuickCreateOrganization from 'views/screens/Organizations/views/listOrganization/SlidingPaneQuickCreateOrganization';
import DropdownInput from '../DropdownInput';
import { DefaultOptions, useMembership } from './hooks/useMembership';

/* -------------------------------------------------------------------------- */
/*                            COMPONENTE MEMBERSHIP                           */
/* -------------------------------------------------------------------------- */
// Lista de opciones que se le puede enviar al componente
// dentro del prop "options":
//   ----------------------------------------
//  | Option                 | Default Value |
//   ----------------------------------------
//  | colSize                |      "3"      |
//  | showAllOperator        |     false     |
//  | showAllOrganization    |     false     |
//  | showAllAgent           |     false     |
//  | showAllDivision        |     false     |
//  | showAllSubdivision     |     false     |
//  | useUserOperator        |     false     |
//  | useUserOrganization    |     false     |
//  | useUserAgent           |     false     |
//  | useUserDivision        |     false     |
//  | useUserSubdivision     |     false     |
//  | useOperator            |     true      |
//  | useOrganization        |     true      |
//  | useAgent               |     false     |
//  | useDivision            |     true      |
//  | useSubdivision         |     true      |
//  | isOperatorDisabled     |     false     |
//  | isOrganizationDisabled |     false     |
//  | isDivisionDisabled     |     false     |
//  | isSubdivisionDisabled  |     false     |
//  | isAgentDisabled        |     false     |
//  | operatorPlaceholder    |     null      |
//  | organizationPlaceholder|     null      |
//  | agentPlaceholder       |     null      |
//  | divisionPlaceholder    |     null      |
//  | subdivisionPlaceholder |     null      |
//   ----------------------------------------
//
// Props adicionales:
//   --------------------------------------------------
//  | Prop                        | Default Value     |
//   --------------------------------------------------
//  | enableQuickCreateOrganization |     false        |
//  | onQuickCreateComplete        |  () => void      |
//   --------------------------------------------------
//
// enableQuickCreateOrganization: Habilita el botón "+" junto al dropdown de organización
//   - Solo se muestra si el usuario tiene el permiso 'organizationAdd' (MB006)
//   - Usa el mismo patrón visual que ScheduleDropdown
// onQuickCreateComplete: Callback que se ejecuta cuando se completa la creación de una nueva organización

interface PropsDropDowns {
  name: any;
  control: any;
  shouldUnregister?: boolean;
  isInModal?: boolean;
  additionalonchangefunction?: (
    newMembership: DefaultFilters,
    options: DefaultOptions | MembershipOptions
  ) => void;
  membershipDefaultValue: any;
  membershipOptions: MembershipOptions;
  enableQuickCreateOrganization?: boolean;
  onQuickCreateComplete?: (organizationData: any) => void;
}

interface Props extends Omit<PropsDropDowns, 'membershipDefaultValue' | 'membershipOptions'> {
  options: DefaultOptions;
  defaultValue?: any;
  fetchEntities?: any;
  showDropdowns?: any;
  enableQuickCreateOrganization?: boolean;
  onQuickCreateComplete?: (organizationData: any) => void;
}

interface MembershipOptions {
  useOperator: boolean;
  useOrganization: boolean;
  useDivision: boolean;
  useSubdivision: boolean;
  useAgent: boolean;
  useGroup: boolean;
  showAllOperator: boolean;
  showAllOrganization: boolean;
  showAllDivision: boolean;
  showAllSubdivision: boolean;
  showAllAgent: boolean;
  showAllGroup: boolean;
  filterOperator: boolean;
  filterOrganization: boolean;
  filterDivision: boolean;
  filterSubdivision: boolean;
  filterGroup: boolean;
  isOperatorDisabled: boolean;
  isOrganizationDisabled: boolean;
  isDivisionDisabled: boolean;
  isSubdivisionDisabled: boolean;
  isAgentDisabled: boolean;
  isGroupDisabled: boolean;
  //
  colSize: number;
  subdivisionPlaceholder: string;
}

const Dropdowns: FC<PropsDropDowns> = (props) => {
  const {
    name,
    control,
    shouldUnregister = false,
    isInModal = false,
    additionalonchangefunction = (newMembership, options) => null,
    membershipDefaultValue,
    membershipOptions,
    enableQuickCreateOrganization = false,
    onQuickCreateComplete = () => null
  } = props;

  const [isOpenQuickCreateOrganization, setOpenQuickCreateOrganization] = useState<boolean>(false);
  const [tooltipOpen, setTooltipOpen] = useState(false);
  const dispatch = useDispatch();

  const toggleTooltip = () => setTooltipOpen(!tooltipOpen);

  const { field: operatorField, fieldState: operatorFieldState } = useController({
    name: name + '.operatorId',
    control: control,
    shouldUnregister: shouldUnregister,
    defaultValue: membershipDefaultValue?.operatorId,
    rules: { required: membershipOptions.useOperator && !membershipOptions.showAllOperator }
  });

  const { field: organizationField, fieldState: organizationFieldState } = useController({
    name: name + '.organizationId',
    control: control,
    shouldUnregister: shouldUnregister,
    defaultValue: membershipDefaultValue?.organizationId,
    rules: { required: membershipOptions.useOrganization && !membershipOptions.showAllOrganization }
  });

  const { field: divisionField, fieldState: divisionFieldState } = useController({
    name: name + '.divisionId',
    control: control,
    shouldUnregister: shouldUnregister,
    defaultValue: membershipDefaultValue?.divisionId,
    rules: { required: membershipOptions.useDivision && !membershipOptions.showAllDivision }
  });

  const { field: subdivisionField, fieldState: subdivisionFieldState } = useController({
    name: name + '.subdivisionId',
    control: control,
    shouldUnregister: shouldUnregister,
    defaultValue: membershipDefaultValue?.subdivisionId,
    rules: { required: membershipOptions.useSubdivision && !membershipOptions.showAllSubdivision }
  });

  const { field: groupField, fieldState: groupFieldState } = useController({
    name: name + '.groupId',
    control: control,
    shouldUnregister: shouldUnregister,
    defaultValue: membershipDefaultValue?.groupId,
    rules: { required: membershipOptions.useGroup && !membershipOptions.showAllGroup }
  });

  const intl = useIntl();
  //Selectors de las listas para los dropdowns
  const {
    hasPermissionToAddOrganization,
    operatorsList,
    organizationsList,
    divisionsList,
    subdivisionsList,
    agentsList,
    groupList
  } = useSelector((state) => ({
    hasPermissionToAddOrganization: authSelectors.getAuthUserHasPermissionsAny(state, [
      organizationPermissions.organizationAdd
    ]),
    operatorsList: operatorSelectors.getOperatorsList(state).map((operator) => ({
      value: operator.operatorId,
      label: operator.operatorName
    })),
    organizationsList: membershipOptions.useOperator
      ? organizationSelectors
          .getOrganizationsListBySelectedOperator(state, {
            operatorId: operatorField.value
          })
          .map((organization) => ({
            value: organization.organizationId,
            label: organization.organizationName
          }))
      : organizationSelectors.getOrganizationsList(state).map((organization) => ({
          value: organization.organizationId,
          label: organization.organizationName
        })),
    divisionsList:
      membershipOptions.useOrganization || membershipOptions.filterOrganization
        ? divisionSelectors
            .getDivisionsListBySelectedOrganization(state, {
              operatorId: operatorField.value,
              organizationId: organizationField.value ?? membershipOptions.filterOrganization
            })
            .map((division) => ({
              value: division.divisionId,
              label: division.divisionName
            }))
        : divisionSelectors.getDivisionsList(state).map((division) => ({
            value: division.divisionId,
            label: division.divisionName
          })),
    subdivisionsList: membershipOptions.useDivision
      ? subdivisionSelectors
          .getSubdivisionsListBySelectedDivision(state, {
            operatorId: operatorField.value,
            organizationId: organizationField.value,
            divisionId: divisionField.value
          })
          .map((subdivision) => ({
            value: subdivision.subdivisionId,
            label: subdivision.subdivisionName
          }))
      : subdivisionSelectors.getSubdivisionsList(state).map((subdivision) => ({
          value: subdivision.subdivisionId,
          label: subdivision.subdivisionName
        })),
    agentsList: membershipOptions.useAgent
      ? agentsSelectors
          .getAgentsListByMembership(state, {
            operatorId: operatorField.value
          })
          .map((agent) => ({
            value: agent.agentId,
            label: agent.agentName
          }))
      : agentsSelectors.getAgentsList(state).map((agent) => ({
          value: agent.agentId,
          label: agent.agentName
        })),
    groupList: groupsSelectors.getGroupsList(state).map((group) => ({
      value: group.groupId,
      label: group.groupName
    }))
  }));

  //Actualizar el modelo que tiene el padre del componente
  useEffect(() => {
    finalOnChange({
      operatorId: operatorField.value,
      organizationId: organizationField.value,
      divisionId: divisionField.value,
      subdivisionId: subdivisionField.value,
      groupId: groupField.value
    });
  }, [
    operatorField.value,
    organizationField.value,
    divisionField.value,
    subdivisionField.value,
    groupField.value
  ]);

  const finalOnChange = (values) => {
    additionalonchangefunction(values, membershipOptions);
  };

  const [lastOrganizationCount, setLastOrganizationCount] = useState(0);
  const [lastDivisionCount, setLastDivisionCount] = useState(0);
  const [lastSubdivisionCount, setLastSubdivisionCount] = useState(0);
  const [quickCreateInProgress, setQuickCreateInProgress] = useState(false);

  // Track organization list changes to detect new creations
  useEffect(() => {
    if (
      organizationsList.length > lastOrganizationCount &&
      lastOrganizationCount > 0 &&
      quickCreateInProgress
    ) {
      // A new organization was likely created
      const newOrganization = organizationsList[organizationsList.length - 1];
      if (newOrganization) {
        // Pre-select the new organization
        organizationField.onChange(newOrganization.value);

        // Reset division and subdivision as they'll be populated when the lists update
        divisionField.onChange(null);
        subdivisionField.onChange(null);
      }
    }
    setLastOrganizationCount(organizationsList.length);
  }, [organizationsList.length, quickCreateInProgress]);

  // Track division list changes
  useEffect(() => {
    if (
      divisionsList.length > lastDivisionCount &&
      lastDivisionCount > 0 &&
      quickCreateInProgress &&
      organizationField.value
    ) {
      // A new division was likely created
      const newDivision = divisionsList[divisionsList.length - 1];
      if (newDivision) {
        divisionField.onChange(newDivision.value);
        subdivisionField.onChange(null); // Reset subdivision
      }
    }
    setLastDivisionCount(divisionsList.length);
  }, [divisionsList.length, quickCreateInProgress, organizationField.value]);

  // Track subdivision list changes
  useEffect(() => {
    if (
      subdivisionsList.length > lastSubdivisionCount &&
      lastSubdivisionCount > 0 &&
      quickCreateInProgress &&
      divisionField.value
    ) {
      // A new subdivision was likely created
      const newSubdivision = subdivisionsList[subdivisionsList.length - 1];
      if (newSubdivision) {
        subdivisionField.onChange(newSubdivision.value);

        // Call the external callback with all the new data
        onQuickCreateComplete({
          organizationId: organizationField.value,
          organizationName: organizationsList.find((org) => org.value === organizationField.value)
            ?.label,
          divisionId: divisionField.value,
          divisionName: divisionsList.find((div) => div.value === divisionField.value)?.label,
          subdivisionId: newSubdivision.value,
          subdivisionName: newSubdivision.label
        });

        setQuickCreateInProgress(false);
      }
    }
    setLastSubdivisionCount(subdivisionsList.length);
  }, [subdivisionsList.length, quickCreateInProgress, divisionField.value]);

  const handleQuickCreateOpen = () => {
    setLastOrganizationCount(organizationsList.length);
    setLastDivisionCount(divisionsList.length);
    setLastSubdivisionCount(subdivisionsList.length);
    setQuickCreateInProgress(true);
    setOpenQuickCreateOrganization(true);
  };

  const handleQuickCreateClose = () => {
    // Refresh entities when the pane closes in case something was created
    dispatch(organizationActions.startFetchingOrganizations());

    // Use a timeout to allow the refresh to complete before resetting state
    setTimeout(() => {
      setOpenQuickCreateOrganization(false);
      if (quickCreateInProgress) {
        // If still in progress after timeout, reset the flag
        setTimeout(() => setQuickCreateInProgress(false), 2000);
      }
    }, 500);
  };
  return (
    <Fragment>
      <FetchEntities
        useDivisions
        divisionsFilters={{
          operatorId: operatorField.value,
          organizationId: organizationField.value ?? membershipOptions.filterOrganization
        }}
        useSubdivisions
        subdivisionsFilters={{
          operatorId: operatorField.value,
          organizationId: organizationField.value ?? membershipOptions.filterOrganization
        }}
        useGroups={membershipOptions.useGroup}
        groupsFilters={{
          operatorId: operatorField.value,
          organizationId: organizationField.value ?? membershipOptions.filterOrganization
        }}
      />
      {membershipOptions.useOperator && (
        <Col sm={membershipOptions.colSize}>
          <DropdownInput
            field={{
              ...operatorField,
              onChange: (value) => {
                if (operatorField.value !== value) {
                  operatorField.onChange(value);
                  organizationField.onChange(null);
                  divisionField.onChange(null);
                  subdivisionField.onChange(null);
                  groupField.onChange(null);
                }
              }
            }}
            fieldstate={operatorFieldState}
            forceinitialvalue={membershipDefaultValue.operatorId}
            required={!membershipOptions.showAllOperator}
            isClearable={membershipOptions.showAllOperator}
            onClear={() => {
              operatorField.onChange(null);
              organizationField.onChange(null);
              divisionField.onChange(null);
              subdivisionField.onChange(null);
              groupField.onChange(null);
            }}
            id={'operatorInput'}
            isInModal={isInModal}
            isDisabled={membershipOptions.isOperatorDisabled}
            options={operatorsList}
            label={intl.formatMessage({
              id: 'operators.operator'
            })}
            placeholder={intl.formatMessage({
              id: membershipOptions.showAllOperator ? 'common.all' : 'operators.selectOperator'
            })}
          />
        </Col>
      )}
      {membershipOptions.useOrganization && (
        <Col sm={membershipOptions.colSize}>
          <DropdownInput
            field={{
              ...organizationField,
              onChange: (value) => {
                if (organizationField.value !== value) {
                  organizationField.onChange(value);
                  divisionField.onChange(null);
                  subdivisionField.onChange(null);
                  groupField.onChange(null);
                }
              }
            }}
            fieldstate={organizationFieldState}
            forceinitialvalue={membershipDefaultValue.organizationId}
            required={!membershipOptions.showAllOrganization}
            isClearable={membershipOptions.showAllOrganization}
            onClear={() => {
              organizationField.onChange(null);
              divisionField.onChange(null);
              subdivisionField.onChange(null);
              groupField.onChange(null);
            }}
            id={'organizationInput'}
            isInModal={isInModal}
            isDisabled={membershipOptions.isOrganizationDisabled}
            options={organizationsList}
            label={intl.formatMessage({
              id: 'organizations.organization'
            })}
            placeholder={intl.formatMessage({
              id: membershipOptions.showAllOrganization
                ? 'common.all'
                : 'organizations.selectOrganization'
            })}
            appendcomponent={
              enableQuickCreateOrganization && hasPermissionToAddOrganization ? (
                <>
                  <Tooltip
                    placement="top"
                    isOpen={tooltipOpen}
                    target={'addOrganization'}
                    toggle={toggleTooltip}
                    trigger="click hover"
                  >
                    <FormattedMessage id="organizations.quickCreate" />
                  </Tooltip>
                  <InputGroupText
                    id={'addOrganization'}
                    style={{ cursor: 'pointer' }}
                    className={'m-0'}
                    onClick={() => handleQuickCreateOpen()}
                  >
                    <Plus size={15} />
                  </InputGroupText>
                </>
              ) : (
                <></>
              )
            }
          />
        </Col>
      )}
      {membershipOptions.useDivision && (
        <Col sm={membershipOptions.colSize}>
          <DropdownInput
            field={{
              ...divisionField,
              onChange: (value) => {
                if (divisionField.value !== value) {
                  divisionField.onChange(value);
                  subdivisionField.onChange(null);
                  groupField.onChange(null);
                }
              }
            }}
            fieldstate={divisionFieldState}
            forceinitialvalue={membershipDefaultValue.divisionId}
            required={!membershipOptions.showAllDivision}
            isClearable={membershipOptions.showAllDivision}
            onClear={() => {
              divisionField.onChange(null);
              subdivisionField.onChange(null);
              groupField.onChange(null);
            }}
            id={'divisionInput'}
            isInModal={isInModal}
            isDisabled={membershipOptions.isDivisionDisabled}
            options={divisionsList}
            label={intl.formatMessage({
              id: 'divisions.division'
            })}
            placeholder={intl.formatMessage({
              id: membershipOptions.showAllDivision ? 'common.all' : 'divisions.selectDivision'
            })}
          />
        </Col>
      )}
      {membershipOptions.useSubdivision && (
        <Col sm={membershipOptions.colSize}>
          <DropdownInput
            field={subdivisionField}
            fieldstate={subdivisionFieldState}
            forceinitialvalue={membershipDefaultValue.subdivisionId}
            required={!membershipOptions.showAllSubdivision}
            isClearable={membershipOptions.showAllSubdivision}
            id={'subdivisionInput'}
            isInModal={isInModal}
            isDisabled={membershipOptions.isSubdivisionDisabled}
            options={subdivisionsList}
            label={intl.formatMessage({
              id: 'subdivisions.subdivision'
            })}
            placeholder={
              membershipOptions.subdivisionPlaceholder ??
              intl.formatMessage({
                id: membershipOptions.showAllSubdivision
                  ? 'common.all'
                  : 'subdivisions.selectSubdivision'
              })
            }
          />
        </Col>
      )}
      {membershipOptions.useAgent && (
        <Col sm={membershipOptions.colSize}>
          <Controller
            control={control}
            shouldUnregister={shouldUnregister}
            name={name + '.agentId'}
            defaultValue={membershipDefaultValue?.agentId}
            rules={{ required: !membershipOptions.showAllAgent }}
            render={({ field, fieldState }) => (
              <DropdownInput
                field={field}
                fieldstate={fieldState}
                forceinitialvalue={membershipDefaultValue.agentId}
                required={!membershipOptions.showAllAgent}
                isClearable={membershipOptions.showAllAgent}
                id={'agentInput'}
                isInModal={isInModal}
                isDisabled={membershipOptions.isAgentDisabled}
                options={agentsList}
                label={intl.formatMessage({
                  id: 'agents.agent'
                })}
                placeholder={intl.formatMessage({
                  id: membershipOptions.showAllAgent ? 'common.all' : 'agents.selectAgent'
                })}
              />
            )}
          />
        </Col>
      )}
      {membershipOptions.useGroup && (
        <Col sm={membershipOptions.colSize}>
          <DropdownInput
            field={{
              ...groupField,
              onChange: (value) => {
                if (groupField.value !== value) {
                  groupField.onChange(value);
                }
              }
            }}
            fieldstate={groupFieldState}
            forceinitialvalue={membershipDefaultValue.groupId}
            required={!membershipOptions.showAllGroup}
            isClearable={membershipOptions.showAllGroup}
            onClear={() => {
              groupField.onChange(null);
            }}
            id={'groupInput'}
            isInModal={isInModal}
            isDisabled={membershipOptions.isGroupDisabled}
            options={groupList}
            label={intl.formatMessage({
              id: 'groups.group'
            })}
            placeholder={intl.formatMessage({
              id: membershipOptions.showAllGroup ? 'common.all' : 'users.selectGroup'
            })}
          />
        </Col>
      )}

      {/* Quick Create Organization Sliding Pane */}
      {enableQuickCreateOrganization && (
        <SlidingPaneQuickCreateOrganization
          isSlidingPaneOpen={isOpenQuickCreateOrganization}
          closeSlidingPane={handleQuickCreateClose}
        />
      )}
    </Fragment>
  );
};

const Membership: FC<Props> = (props) => {
  const {
    options = {},
    defaultValue = {
      groupId: null,
      agentId: null,
      operatorId: null,
      organizationId: null,
      divisionId: null,
      subdivisionId: null,
      operatorName: '',
      organizationName: '',
      divisionName: '',
      subdivisionName: '',
      agentName: '',
      groupName: ''
    },
    fetchEntities = true,
    showDropdowns = true,
    additionalonchangefunction,
    enableQuickCreateOrganization = false,
    onQuickCreateComplete
  } = props;
  //Se inicializa model y options
  const [membershipDefaultValue, membershipOptions, isInitialize] = useMembership(
    options,
    defaultValue
  );

  return (
    <Fragment>
      {fetchEntities && <FetchEntities useOperators useOrganizations />}
      {showDropdowns &&
        (isInitialize ? (
          <Dropdowns
            {...props}
            membershipDefaultValue={membershipDefaultValue}
            membershipOptions={membershipOptions}
            enableQuickCreateOrganization={enableQuickCreateOrganization}
            onQuickCreateComplete={onQuickCreateComplete}
          />
        ) : (
          <Col sm={membershipOptions.colSize} className="d-flex justify-content-center mt-1 mb-1">
            <Spinner color="primary" size="lg" />
          </Col>
        ))}
    </Fragment>
  );
};

export default Membership;
